import { CheckCircle2, Crown, Download, Sparkles, Calendar, CreditCard, User, Tag } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  formatAmountPaise,
  formatMembershipDate,
  parseMembershipDate,
  PREMIUM_DURATION_LABEL,
} from "../../utils/membership";

export default function PremiumReceipt({ receipt, celebrate = false }) {
  if (!receipt) return null;

  const paidAt = parseMembershipDate(receipt.paidAt);
  const validFrom = parseMembershipDate(receipt.validFrom);
  const validUntil = parseMembershipDate(receipt.validUntil);
  const amountLabel =
    receipt.amount != null
      ? formatAmountPaise(receipt.amount, receipt.currency)
      : "-";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="overflow-hidden rounded-3xl bg-white shadow-elevated"
    >
      {/* Hero celebration */}
      {celebrate && (
        <div className="relative overflow-hidden bg-brand-gradient px-6 pb-8 pt-10 text-center text-white">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-amber-200/15 blur-3xl" />

          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="relative mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-white/15 ring-4 ring-white/10"
          >
            <CheckCircle2 size={40} className="text-emerald-300" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <span className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest backdrop-blur">
              <Sparkles size={11} /> Payment successful
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold">You're Premium!</h2>
            <p className="mt-2 text-[14px] leading-6 text-white/85">
              Welcome to Karanki Premium. Enjoy unlimited access to all features.
            </p>
          </motion.div>
        </div>
      )}

      {/* Amount highlight */}
      <div className="flex items-center justify-between border-b border-sand-200 bg-emerald-50 px-5 py-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
            <Crown size={20} />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-600">Amount paid</p>
            <p className="font-display text-2xl font-bold text-emerald-700">{amountLabel}</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold uppercase text-emerald-700">
          <CheckCircle2 size={12} /> Paid
        </span>
      </div>

      {/* Receipt details */}
      <div className="px-5 py-4">
        <div className="space-y-3.5">
          <Row Icon={User} label="Member" value={receipt.memberName || "-"} />
          <Row Icon={Crown} label="Plan" value={receipt.planName || "Karanki Premium"} />
          <Row Icon={Calendar} label="Duration" value={receipt.durationLabel || PREMIUM_DURATION_LABEL} />
          <Row Icon={Calendar} label="Valid from" value={formatMembershipDate(validFrom)} />
          <Row Icon={Calendar} label="Valid until" value={formatMembershipDate(validUntil)} highlight />

          {receipt.couponCode && (
            <Row Icon={Tag} label="Coupon" value={`${receipt.couponCode} (−${formatAmountPaise(receipt.discountAmount, receipt.currency)})`} highlight />
          )}

          {receipt.paymentMethod && (
            <Row Icon={CreditCard} label="Paid via" value={receipt.paymentMethodDetail ? `${receipt.paymentMethod} · ${receipt.paymentMethodDetail}` : receipt.paymentMethod} />
          )}

          <div className="rounded-2xl bg-sand-50 px-4 py-3">
            <div className="flex items-center justify-between text-[12px] text-ink-400">
              <span>Payment ID</span>
              <span className="font-mono text-[11.5px] font-semibold text-ink-600">{receipt.receiptId || "-"}</span>
            </div>
            {receipt.orderId && receipt.orderId !== "-" && (
              <div className="mt-1.5 flex items-center justify-between text-[12px] text-ink-400">
                <span>Order ID</span>
                <span className="font-mono text-[11.5px] font-semibold text-ink-600">{receipt.orderId}</span>
              </div>
            )}
            <div className="mt-1.5 flex items-center justify-between text-[12px] text-ink-400">
              <span>Paid on</span>
              <span className="font-semibold text-ink-600">{formatMembershipDate(paidAt)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="border-t border-sand-200 px-5 py-4">
        <div className="grid grid-cols-2 gap-3">
          <Link to="/matches" className="btn-primary py-3 text-[13px]">
            <Sparkles size={14} /> Find matches
          </Link>
          <Link to="/settings/payments" className="btn-ghost py-3 text-[13px]">
            <Download size={14} /> View receipts
          </Link>
        </div>
        <p className="mt-3 text-center text-[11.5px] text-ink-400">
          Premium valid until <span className="font-semibold text-brand-600">{formatMembershipDate(validUntil)}</span>. Renew anytime from settings.
        </p>
      </div>
    </motion.div>
  );
}

function Row({ Icon, label, value, highlight }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-sand-100 text-ink-400">
        <Icon size={14} />
      </div>
      <div className="flex flex-1 items-center justify-between gap-2">
        <span className="text-[12.5px] text-ink-400">{label}</span>
        <span className={`text-right text-[13px] font-semibold ${highlight ? "text-brand-600" : "text-ink-800"}`}>
          {value}
        </span>
      </div>
    </div>
  );
}
